import * as _ from "lodash";

import {
  ProjectInterface as IProject,
  SyncAction,
} from "./interfaces/project.interface";

import { ProjectFormAreaCreateInterface as IAreaCreate } from "./interfaces/projectFormAreaCreate/projectFormAreaCreate.interface";
import { ProjectFormChecklistInterface as IChecklist } from "./interfaces/projectFormChecklist/projectFormChecklist.interface";
import { ProjectFormFinishInterface as IFinish } from "./interfaces/projectFormFinish/projectFormFinish.interface";
import { ProjectFormMaterialsInterface as IMaterials } from "./interfaces/projectFormMaterials/projectFormMaterials.interface";
import { ProjectFormMonstersInterface as IMonsters } from "./interfaces/projectFormMonsters/projectFormMonsters.interface";
import { ProjectFormInterface as IProjectForm } from "./interfaces/projectForm/projectFormInterface.interface";

const initialState: IProject = {
  id: undefined,
  isCopy: false,
  isCopyFrom: undefined,
  projectId: undefined,
  saved: undefined,
  createdAt: undefined,
  lastSyncedAt: undefined,
  syncAction: undefined,
  owner: undefined,
  dirty: undefined,
  archived: undefined,
  projectForm: {
    completed: undefined,
    fields: {
      settings: {
        projectNumber: undefined,
        projectName: undefined,
        clientName: undefined,
        clientAddress: undefined,
        clientZipCode: undefined,
        clientLocation: undefined,
        clientContactPerson: undefined,
      },
      object: {
        address: undefined,
        location: undefined,
        architecturalUnit: undefined,
        constructionYear: undefined,
      },
      historical: {
        reconstruction: undefined,
        data: undefined,
        remediated: undefined,
        sanitizedMaterials: undefined,
        other: undefined,
        inventoryReason: undefined,
      },
      drawings: {
        construction: undefined,
        map: undefined,
        installations: undefined,
        specifications: undefined,
      },
      contact: {
        inUse: undefined,
        inUseDescription: undefined,
        administratorName: undefined,
        administratorPhoneNumber: undefined,
        interviewPossible: undefined,
        interviewName: undefined,
        involvement: undefined,
        interviewPhoneNumber: undefined,
      },
      other: {
        spacesMaterialsAccessible: undefined,
        guidance: undefined,
        detailsHO: undefined,
        description: undefined,
      },
      safety: {
        areaAsbestosContamination: undefined,
        expectedAsbestos: [],
        additionalMeasures: undefined,
      },
      execution: {
        conformAsbestos: undefined,
        specialProcess: undefined,
        documentsPresent: undefined,
        planMeetsSituation: undefined,
        objectMeetsFeatures: undefined,
      },
      verdict: {
        canStartAsbestosInvetory: undefined,
        description: undefined,
        specifications: undefined,
      },
    },
  },
  projectFormAreaCreate: {
    completed: undefined,
    fields: {
      building: {
        surface: undefined,
        locationType: undefined,
        addresses: undefined,
      },
    },
  },
  projectFormChecklist: {
    completed: undefined,
    fields: {
      installations: {
        heating: undefined,
        sewage: undefined,
        smokeDuct: undefined,
        ventilation: undefined,
        electricity: undefined,
        drainage: undefined,
        steamPipes: undefined,
        verticalTransport: undefined,
        water: undefined,
      },
      pipes: {
        brand: undefined,
        pipeProtection: undefined,
        type: undefined,
        typeForAll: undefined,
        constructionYear: undefined,
        constructionYearForAll: undefined,
      },
      equipment: {
        blackboard: undefined,
        fumeCupboard: undefined,
        emergencyPower: undefined,
      },
      inflammableConstruct: {
        fireCompartmentV: undefined,
        fireCompartmentH: undefined,
        fireResistantLoadbearingStruct: undefined,
        fireCompartmentCollision: undefined,
      },
      finishings: {
        stucco: undefined,
        carpet: undefined,
        filling: undefined,
        mobileWalls: undefined,
        stoneSub: undefined,
        imitationMaterials: undefined,
      },
      facade: {
        corrugatedSheets: undefined,
        waterRetainVaporLayers: undefined,
        roofing: undefined,
        stoneSub: undefined,
        wallCladding: undefined,
      },
      asbestosStructure: {
        adjustmentPlates: undefined,
        formwork: undefined,
      },
      frames: {
        steel: undefined,
        aluminium: undefined,
        destructive: undefined,
      },
      agriculture: {
        cesspit: undefined,
        concreteFloor: undefined,
        corrugatedSheetsSeparation: undefined,
        destructive: undefined,
      },
      other: {
        asbestosContamination: undefined,
      },
    },
  },
  projectFormFinish: {
    completed: undefined,
    fields: {
      execution: {
        fullInspection: undefined,
        inspectedLocations: undefined,
        inspectionRestrictions: undefined,
        restrictions: undefined,
        inaccessibleLocations: undefined,
        accordingToPlan: undefined,
        inaccessibleLocationEntries: [],
        deviations: undefined,
        otherObservations: undefined,
        observations: undefined,
      },
    },
  },
  projectFormMaterials: {
    completed: undefined,
    fields: {
      pbm: {
        fullFaceMask: undefined,
        helmet: undefined,
        overall: undefined,
        halfFaceMask: undefined,
        oxygenMeter: undefined,
        earplugs: undefined,
        safetyGlasses: undefined,
        absoluteVacuumCleaner: undefined,
        safetyShoes: undefined,
        firstAid: undefined,
      },
      tools: {
        camera: undefined,
        crowbar: undefined,
        ductTape: undefined,
        flashlight: undefined,
        hammer: undefined,
        semStubs: undefined,
        ladder: undefined,
        chisel: undefined,
        sampleBag: undefined,
        asbestosWarningTape: undefined,
        drill: undefined,
        gloves: undefined,
        nippers: undefined,
        magnet: undefined,
        measuringTape: undefined,
        combinationPliers: undefined,
        cordlessDrill: undefined,
        cornerMirror: undefined,
        waterPumpPliers: undefined,
        constructionLamp: undefined,
        tissues: undefined,
        knife: undefined,
        endoscope: undefined,
        asbestosStickers: undefined,
        screwdriver: undefined,
        glue: undefined,
        wasteBags: undefined,
        certification: undefined,
      },
    },
  },
  projectFormMonsters: {
    completed: undefined,
    fields: {
      monsters: [],
    },
  },
};

export class Project {
  public id: number;
  public projectId: number;
  public isCopy: boolean;
  public isCopyFrom: number;
  public saved: boolean;
  public createdAt: string;
  public lastSyncedAt: string;
  public syncAction: SyncAction;
  public owner: string;
  public dirty: boolean;
  public archived: boolean;
  public projectForm: IProjectForm;
  public projectFormAreaCreate: IAreaCreate;
  public projectFormChecklist: IChecklist;
  public projectFormFinish: IFinish;
  public projectFormMaterials: IMaterials;
  public projectFormMonsters: IMonsters;

  static create(data?: Partial<IProject>): Project {
    return new Project(data);
  }

  constructor(init?: Partial<IProject>) {
    Object.assign(this, initialState, init);
  }

  static formatResponse(res) {
    let formatted: IProject = _.cloneDeep(initialState);

    formatted.id = res.project.id;
    formatted.isCopy = res.project.is_copy;
    formatted.isCopyFrom = res.project.is_copy_from;
    formatted.saved = res.project.saved;
    formatted.createdAt = res.project.created_at;
    formatted.lastSyncedAt = res.project.last_synced_at;
    formatted.syncAction = res.project.sync_action;
    formatted.owner = res.project.owner;
    formatted.archived = res.project.archived;
    formatted.dirty = false;

    let formattedAddresses = [];
    let formattedInspectedAddresses = [];
    let formattedInnaccessibleAddresses = [];

    for (let address of res.addresses) {
      let formattedAddress = {
        id: address.id,
        isCopy: address.is_copy,
        isCopyFrom: address.is_copy_from,
        saved: address.saved,
        locationType: address.location_type,
        address: address.address,
        house: address.house,
        floor: address.floor,
        notInspectedReason: address.not_inspected_reason,
      };

      formattedAddresses.push(formattedAddress);

      if (address.not_inspected) {
        formattedInspectedAddresses.push(formattedAddress);
      }

      if (address.inaccessible) {
        formattedInnaccessibleAddresses.push(formattedAddress);
      }
    }

    let formattedExpectedAsbestos = [];
    if(res.inventarisation) {
      formattedExpectedAsbestos = (res.inventarisation.expected_asbestos
        ? res.inventarisation.expected_asbestos
        : []
      ).map((item) => ({
        id: item.id,
        saved: true,
        type: item.type,
        concentration: item.concentration,
      }));
    }

    formatted.projectForm = {
      // exception
      completed: res.project.inventarisation_completed,
      fields: {
        settings: {
          projectNumber: res.project.project_number,
          projectName: res.project.project_name,
          clientName: res.project.client_name,
          clientAddress: res.project.client_address,
          clientZipCode: res.project.client_zip_code,
          clientLocation: res.project.client_location,
          clientContactPerson: res.project.client_contact_person,
        },
        object: {
          address: res.project.address,
          location: res.project.location,
          architecturalUnit: res.project.architectural_unit,
          constructionYear: res.project.construction_year,
        },
        historical: {
          reconstruction: (res.inventarisation ? res.inventarisation.reconstruction : null),
          data: (res.inventarisation ? res.inventarisation.data : null),
          remediated: (res.inventarisation ? res.inventarisation.remediated : null),
          sanitizedMaterials: (res.inventarisation ? res.inventarisation.sanitized_materials : null),
          other: (res.inventarisation ? res.inventarisation.other : null),
          inventoryReason: (res.inventarisation ? res.inventarisation.inventory_reason : null),
        },
        drawings: {
          construction: (res.inventarisation ? res.inventarisation.construction : null),
          map: (res.inventarisation ? res.inventarisation.map : null),
          installations: (res.inventarisation ? res.inventarisation.installations : null),
          // Exception
          specifications: (res.inventarisation ? res.inventarisation.drawing_specifications : null),
        },
        contact: {
          inUse: (res.inventarisation ? res.inventarisation.in_use : null),
          inUseDescription: (res.inventarisation ? res.inventarisation.in_use_description : null),
          administratorName: (res.inventarisation ? res.inventarisation.administrator_name : null),
          administratorPhoneNumber:
            (res.inventarisation ? res.inventarisation.administrator_phone_number : null),
          interviewPossible: (res.inventarisation ? res.inventarisation.interview_possible : null),
          interviewName: (res.inventarisation ? res.inventarisation.interview_name : null),
          involvement: (res.inventarisation ? res.inventarisation.involvement : null),
          interviewPhoneNumber: (res.inventarisation ? res.inventarisation.interview_phone_number : null),
        },
        other: {
          spacesMaterialsAccessible:
            (res.inventarisation ? res.inventarisation.spaces_materials_accessible : null),
          guidance: (res.inventarisation ? res.inventarisation.guidance : null),
          detailsHO: (res.inventarisation ? res.inventarisation.details_ho : null),
          // Exception
          description: (res.inventarisation ? res.inventarisation.other_description : null),
        },
        safety: {
          areaAsbestosContamination:
            (res.inventarisation ? res.inventarisation.area_asbestos_contamination : null),
          expectedAsbestos: formattedExpectedAsbestos,
          additionalMeasures: (res.inventarisation ? res.inventarisation.additional_measures : null),
        },
        execution: {
          conformAsbestos: (res.inventarisation ? res.inventarisation.conform_asbestos : null),
          specialProcess: (res.inventarisation ? res.inventarisation.special_process : null),
          documentsPresent: (res.inventarisation ? res.inventarisation.documents_present : null),
          planMeetsSituation: (res.inventarisation ? res.inventarisation.plan_meets_situation : null),
          objectMeetsFeatures: (res.inventarisation ? res.inventarisation.object_meets_features : null),
        },
        verdict: {
          canStartAsbestosInvetory:
            (res.inventarisation ? res.inventarisation.can_start_asbestos_invetory : null),
          // Exception
          description: (res.inventarisation ? res.inventarisation.verdict_description : null),
          // Exception
          specifications: (res.inventarisation ? res.inventarisation.verdict_specifications : null),
        },
      },
    };
    formatted.projectFormAreaCreate = {
      // exception
      completed: res.project.area_create_completed,
      fields: {
        building: {
          surface: (res.inventarisation ? res.inventarisation.surface : null),
          locationType: (res.inventarisation ? res.inventarisation.location_type : null),
          addresses:
            formattedAddresses && formattedAddresses.length > 0
              ? formattedAddresses
              : undefined,
        },
      },
    };
    formatted.projectFormChecklist = {
      // exception
      completed: res.project.checklist_completed,
      fields: {
        installations: {
          heating: (res.checklist ? res.checklist.heating: null),
          sewage: (res.checklist ? res.checklist.sewage: null),
          smokeDuct: (res.checklist ? res.checklist.smoke_duct: null),
          ventilation: (res.checklist ? res.checklist.ventilation: null),
          electricity: (res.checklist ? res.checklist.electricity: null),
          drainage: (res.checklist ? res.checklist.drainage: null),
          steamPipes: (res.checklist ? res.checklist.steam_pipes: null),
          verticalTransport: (res.checklist ? res.checklist.vertical_transport: null),
          water: (res.checklist ? res.checklist.water: null),
        },
        pipes: {
          brand: (res.checklist ? res.checklist.brand: null),
          pipeProtection: (res.checklist ? res.checklist.pipe_protection: null),
          type: (res.checklist ? res.checklist.type: null),
          typeForAll: (res.checklist ? res.checklist.type_for_all: null),
          constructionYear: (res.checklist ? res.checklist.construction_year: null),
          constructionYearForAll: (res.checklist ? res.checklist.construction_year_for_all: null),
        },
        equipment: {
          blackboard: (res.checklist ? res.checklist.blackboard: null),
          fumeCupboard: (res.checklist ? res.checklist.fume_cupboard: null),
          emergencyPower: (res.checklist ? res.checklist.emergency_power: null),
        },
        inflammableConstruct: {
          fireCompartmentV: (res.checklist ? res.checklist.fire_compartment_v: null),
          fireCompartmentH: (res.checklist ? res.checklist.fire_compartment_h: null),
          fireResistantLoadbearingStruct:
            (res.checklist ? res.checklist.fire_resistant_loadbearing_struct: null),
          fireCompartmentCollision: (res.checklist ? res.checklist.fire_compartment_collision: null),
        },
        finishings: {
          stucco: (res.checklist ? res.checklist.stucco: null),
          carpet: (res.checklist ? res.checklist.carpet: null),
          filling: (res.checklist ? res.checklist.filling: null),
          mobileWalls: (res.checklist ? res.checklist.mobile_walls: null),
          // exception
          stoneSub: (res.checklist ? res.checklist.finish_stone_sub: null),
          imitationMaterials: (res.checklist ? res.checklist.imitation_materials: null),
        },
        facade: {
          corrugatedSheets: (res.checklist ? res.checklist.corrugated_sheets: null),
          waterRetainVaporLayers: (res.checklist ? res.checklist.water_retain_vapor_layers: null),
          roofing: (res.checklist ? res.checklist.roofing: null),
          // exception
          stoneSub: (res.checklist ? res.checklist.facade_stone_sub: null),
          wallCladding: (res.checklist ? res.checklist.wall_cladding: null),
        },
        asbestosStructure: {
          adjustmentPlates: (res.checklist ? res.checklist.adjustment_plates: null),
          formwork: (res.checklist ? res.checklist.formwork: null),
        },
        frames: {
          steel: (res.checklist ? res.checklist.steel: null),
          aluminium: (res.checklist ? res.checklist.aluminium: null),
          // exception
          destructive: (res.checklist ? res.checklist.frame_destructive: null),
        },
        agriculture: {
          cesspit: (res.checklist ? res.checklist.cesspit: null),
          concreteFloor: (res.checklist ? res.checklist.concrete_floor: null),
          corrugatedSheetsSeparation:
            (res.checklist ? res.checklist.corrugated_sheets_separation: null),
          // exception
          destructive: (res.checklist ? res.checklist.agri_destructive: null),
        },
        other: {
          asbestosContamination: (res.checklist ? res.checklist.asbestos_contamination: null),
        },
      },
    };

    let formattedInaccessibleLocationEntries = [];
    if(res.finish) {
      formattedInaccessibleLocationEntries = (res.finish
        .inaccessible_location_entries
        ? res.finish.inaccessible_location_entries
        : []
      ).map((item) => ({
        id: item.id,
        saved: true,
        location: item.location,
        researchMethod: item.research_method,
        foundMaterial: item.found_material,
        inspected: !!item.inspected,
      }));
    }

    if(res.finish) {
      formatted.projectFormFinish = {
        // exception
        completed: res.project.finish_completed,
        fields: {
          execution: {
            fullInspection: !!res.finish.full_inspection,
            inspectedLocations:
              formattedInspectedAddresses &&
              formattedInspectedAddresses.length > 0
                ? formattedInspectedAddresses
                : undefined,
            inspectionRestrictions: res.finish.inspection_restrictions,
            restrictions: res.finish.restrictions,
            inaccessibleLocations: res.finish.inaccessible_locations,
            accordingToPlan: res.finish.according_to_plan,
            inaccessibleLocationEntries: formattedInaccessibleLocationEntries,
            deviations: res.finish.deviations,
            otherObservations: res.finish.other_observations,
            observations: res.finish.observations,
          },
        },
      };
    }

    if(res.material) {
      formatted.projectFormMaterials = {
        // exception
        completed: res.project.materials_completed,
        fields: {
          pbm: {
            fullFaceMask: res.material.full_face_mask,
            helmet: res.material.helmet,
            overall: res.material.overall,
            halfFaceMask: res.material.half_face_mask,
            oxygenMeter: res.material.oxygen_meter,
            earplugs: res.material.earplugs,
            safetyGlasses: res.material.safety_glasses,
            absoluteVacuumCleaner: res.material.absolute_vacuum_cleaner,
            safetyShoes: res.material.safety_shoes,
            firstAid: res.material.first_aid,
          },
          tools: {
            camera: res.material.camera,
            crowbar: res.material.crowbar,
            ductTape: res.material.duct_tape,
            flashlight: res.material.flashlight,
            hammer: res.material.hammer,
            semStubs: res.material.sem_stubs,
            ladder: res.material.ladder,
            chisel: res.material.chisel,
            sampleBag: res.material.sample_bag,
            asbestosWarningTape: res.material.asbestos_warning_tape,
            drill: res.material.drill,
            gloves: res.material.gloves,
            nippers: res.material.nippers,
            magnet: res.material.magnet,
            measuringTape: res.material.measuring_tape,
            combinationPliers: res.material.combination_pliers,
            cordlessDrill: res.material.cordless_drill,
            cornerMirror: res.material.corner_mirror,
            waterPumpPliers: res.material.water_pump_pliers,
            constructionLamp: res.material.construction_lamp,
            tissues: res.material.tissues,
            knife: res.material.knife,
            endoscope: res.material.endoscope,
            asbestosStickers: res.material.asbestos_stickers,
            screwdriver: res.material.screwdriver,
            glue: res.material.glue,
            wasteBags: res.material.waste_bags,
            certification: res.material.certification,
          },
        },
      };
    }

    let formattedMonsters = [];
    for (let monster of res.monsters) {
      let formattedLocations = [];
      for (let location of monster.addresses) {
        formattedLocations.push({
          id: location.id,
          saved: location.saved,
          locationType: location.location_type,
          address: location.address,
          house: location.house,
          floor: location.floor,
        });
      }

      let formattedFiles = [];

      // These are the the photos being loaded from the back-end.
      for (let file of monster.photos) {
        formattedFiles.push({
          id: file.id,
          saved: file.saved,
          src: file.src,
          fields: {
            filename: file.filename,
            filetype: file.filetype,
            value: file.value,
            softDeleted: file.softDeleted,
          },
        });
      }

      let formattedAsbestosInfos = [];

      // Sjors: sometimes this is "info", sometimes "infos";
      // Martijn: in the new situation it's the lowercased version
      const asbestosInfo =
        monster.asbestInfo || monster.asbestInfos || monster.asbest_infos || [];

      for (let asbestInfo of asbestosInfo) {
        formattedAsbestosInfos.push({
          id: asbestInfo.id,
          saved: asbestInfo.saved,
          asbestosType: asbestInfo.type,
          asbestosPercentage: asbestInfo.percentage,
        });
      }

      formattedMonsters.push({
        id: monster.id,
        isCopy: monster.is_copy,
        isCopyFrom: monster.is_copy_from,
        saved: monster.saved,
        monsterId: monster.id,
        smartId: monster.smart_id,
        RPSProcessed: monster.RPSProcessed,
        fields: {
          general: {
            diaId: monster.dia_id,
            dia: monster.dia,
            samplingTime: monster.sampling_time,
            samplingDate: monster.sampling_date,
            sampleCode: monster.g_sample_code,
            accessibility: monster.accessibility,
            addressRemark: monster.address_remark,
            locations: formattedLocations,
          },
          photos: {
            files: formattedFiles,
          },
          material: {
            materialType: monster.material_type,
            materialTypeCustom: monster.material_type_custom,
            materialTypeFull: monster.material_type_full,
            confirmation: monster.confirmation,
            confirmationFull: monster.confirmation_full,
            // confirmationOther: monster.confirmation_other,
            quantity: monster.quantity,
            quantityUnits: monster.quantity_units,
            operation: monster.operation,
            operationFull: monster.operationFull,
            validationExecuted: monster.validation_executed,
            dimensions: monster.dimensions,
            attachment: monster.attachment,
            damages: monster.damages,
            weathering: monster.weathering,
            risk: monster.risk,
            location: monster.location,
            removal: monster.removal,
            riskAlt: monster.risk_alt,
            locationAlt: monster.location_alt,
            removalAlt: monster.removal_alt,
            questions: monster.questions,
          },
          sample: {
            sampleTaken: monster.sample_taken,
            vacuumCleanerUsed: monster.vacuum_cleaner_used,
            humidified: monster.humidified,
            pbmsUsed: monster.pbms_used,
            disposableCover: monster.disposable_cover,
          },
          result: {
            certificateCode: monster.certificate_code,
            // exception
            sampleCode: monster.r_sample_code,
            asbestosInfo: formattedAsbestosInfos,
          },
          comments: {
            observations: monster.observations,
          },
        },
      });
    }
    formatted.projectFormMonsters = {
      // exception
      completed: res.project.monsters_completed,
      fields: {
        monsters: formattedMonsters,
      },
    };

    return formatted;
  }
}



// WEBPACK FOOTER //
// ./src/models/project.model.ts